import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { checkoutTimes, returnToCheckoutStage, personalDetailsPagePath, findVehiclePath } from "@cinch-labs/shared-util";
import { Text } from "@cinch-labs/design-system";
import { useState, useEffect, useCallback, useMemo } from "react";
import styles from "../return-to-checkout-modal.module.css";
import { TimeIcon } from "../image-components/time";
import { i18n } from "../return-to-checkout-modal.component";
import { ModalState } from "../return-to-checkout-modal.types";
import { TrackingEventTypes } from "../types/analytics";
import { useReturnToCheckoutModalTracking } from "../return-to-checkout-modal.tracking";
const hasExpired = (expiresAt)=>expiresAt === undefined ? false : Date.now() > expiresAt;
export const useReturnToCheckoutModal = ({ activeOrder , timeRemainingInSeconds , withinExpiryTimeWindow , checkIfVehicleIsReserved , accessToken , extendOrderExpiryDate , refreshActiveOrder , cancelOrder , onClose , isLoggedIn , handleShouldShowBasketIcon , lastVisitedCheckoutStage , trackEvent  })=>{
    const { resumeCheckoutClosedModal , resumeCheckoutReturnedToCheckout , iDoNotWantThisCarEvent , returnToCheckoutModalEvent , resumeCheckoutOpenedModal  } = useReturnToCheckoutModalTracking(trackEvent ?? noOpTracking);
    const vehicle = useMemo(()=>{
        return activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.vehicle;
    }, [
        activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.vehicle
    ]);
    const orderId = useMemo(()=>{
        return activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.id;
    }, [
        activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.id
    ]);
    const [currentOrder, setCurrentOrder] = useState(activeOrder);
    const [buttonLoading, setButtonLoading] = useState(false);
    const [secondaryButtonLoading, setSecondaryButtonLoading] = useState(false);
    const [vehicleIsReserved, setVehicleIsReserved] = useState(false);
    const [modalLoading, setModalLoading] = useState(false);
    const [timeLeft, setTimeLeft] = useState(timeRemainingInSeconds);
    const [expiringSoon, setExpiringSoon] = useState(withinExpiryTimeWindow);
    const [orderHasExpired, setOrderHasExpired] = useState(hasExpired((activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.expiresAt) ?? (activeOrder === null || activeOrder === void 0 ? void 0 : activeOrder.cancelledAt)));
    useEffect(()=>{
        setOrderHasExpired(hasExpired((currentOrder === null || currentOrder === void 0 ? void 0 : currentOrder.expiresAt) ?? (currentOrder === null || currentOrder === void 0 ? void 0 : currentOrder.cancelledAt)));
        if (orderHasExpired) {
            return;
        }
        const id = setTimeout(()=>{
            const times = checkoutTimes(activeOrder);
            setTimeLeft(times.timeRemainingInSeconds);
            setExpiringSoon(!!times.isExpiringSoon);
        }, 1000);
        return ()=>clearTimeout(id);
    }, [
        timeLeft
    ]);
    useEffect(()=>{
        if (activeOrder && orderHasExpired && checkIfVehicleIsReserved) {
            setModalLoading(true);
            const getReservationStatus = async ()=>{
                const reservationStatus = await checkIfVehicleIsReserved(activeOrder.vehicle.id);
                setVehicleIsReserved(reservationStatus === null || reservationStatus === void 0 ? void 0 : reservationStatus.reserved);
                setModalLoading(false);
            };
            getReservationStatus();
        }
    }, [
        activeOrder,
        orderHasExpired,
        vehicle,
        checkIfVehicleIsReserved
    ]);
    const extendExpiry = useCallback(async ()=>{
        if (!accessToken) {
            return;
        }
        if (orderId) {
            setButtonLoading(true);
            await (extendOrderExpiryDate === null || extendOrderExpiryDate === void 0 ? void 0 : extendOrderExpiryDate(orderId, accessToken));
            setButtonLoading(false);
        }
    }, [
        orderId,
        extendOrderExpiryDate,
        accessToken
    ]);
    const removeOrderForUser = async (token)=>{
        const cancelOrderReason = "Return to Checkout - I don't want this car";
        if (!refreshActiveOrder) {
            return;
        }
        try {
            var ref;
            const orderDetails = activeOrder && await (cancelOrder === null || cancelOrder === void 0 ? void 0 : cancelOrder(activeOrder.id, cancelOrderReason, token));
            switch((orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.status) || ((ref = // eslint-disable-next-line @typescript-eslint/no-explicit-any
            orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.data) === null || ref === void 0 ? void 0 : ref.orderStatus) === "Cancelled"){
                case 200:
                    setCurrentOrder(orderDetails === null || orderDetails === void 0 ? void 0 : orderDetails.data);
                    refreshActiveOrder();
                    onClose();
                    handleShouldShowBasketIcon();
                    break;
                case 403:
                    handleShouldShowBasketIcon();
                    window.location.assign("/login");
                    break;
                default:
                    refreshActiveOrder();
                    break;
            }
            return setSecondaryButtonLoading(false);
        } catch  {
            // eslint-disable-next-line unicorn/no-useless-undefined
            setCurrentOrder(undefined);
            refreshActiveOrder();
            return setSecondaryButtonLoading(false);
        }
    };
    const handleRemoveOrder = async ()=>{
        iDoNotWantThisCarEvent();
        setSecondaryButtonLoading(true);
        const isUserValid = accessToken && isLoggedIn;
        if (!isUserValid) {
            window.location.assign("/login");
        } else {
            await removeOrderForUser(accessToken);
        }
    };
    const onModalOpen = (modalState)=>{
        resumeCheckoutOpenedModal(modalState);
    };
    const onModalClose = (modalState)=>{
        resumeCheckoutClosedModal(modalState);
        onClose();
    };
    const onButtonClick = async ()=>{
        returnToCheckoutModalEvent();
        await extendExpiry();
        returnToCheckoutStage((lastVisitedCheckoutStage === null || lastVisitedCheckoutStage === void 0 ? void 0 : lastVisitedCheckoutStage.stage) ?? "PersonalDetails", activeOrder);
    };
    const onButtonClickExpiredOrderVehicleStillAvailable = ()=>{
        resumeCheckoutReturnedToCheckout(ModalState.expiredStillAvailable);
        vehicle && window.location.assign(`${personalDetailsPagePath}${vehicle.id}`);
    };
    const onButtonClickExpiredOrderVehicleUnavailable = ()=>{
        refreshActiveOrder && refreshActiveOrder();
        resumeCheckoutReturnedToCheckout(ModalState.expiredUnavailable);
        window.location.assign(findVehiclePath);
    };
    const onModalCloseExpiredOrderVehicleUnavailable = ()=>{
        refreshActiveOrder && refreshActiveOrder();
        resumeCheckoutClosedModal(ModalState.expiredStillAvailable);
        onClose();
    };
    return {
        vehicle,
        orderHasExpired,
        timeLeft,
        buttonLoading,
        secondaryButtonLoading,
        vehicleIsReserved,
        modalLoading,
        expiringSoon,
        handleRemoveOrder,
        onModalOpen,
        onModalClose,
        onButtonClick,
        onButtonClickExpiredOrderVehicleStillAvailable,
        onButtonClickExpiredOrderVehicleUnavailable,
        onModalCloseExpiredOrderVehicleUnavailable
    };
};
export const noOpTracking = (event)=>{
    console.log(`noop::trackEvent::${event.type !== TrackingEventTypes.IDENTIFY && event.eventName}`, event);
};
export const styleCardTitle = (title, icon)=>{
    if (icon) {
        return /*#__PURE__*/ _jsx(Text, {
            element: "h5",
            className: styles.cardTitle,
            children: /*#__PURE__*/ _jsxs("div", {
                className: styles.titleWithIconContainer,
                "data-testid": "time-icon-modal-title",
                children: [
                    /*#__PURE__*/ _jsx(TimeIcon, {
                        className: styles.timeIcon
                    }),
                    title
                ]
            })
        });
    }
    return /*#__PURE__*/ _jsx(Text, {
        element: "h5",
        className: styles.cardTitle,
        children: title
    });
};
export const getTitle = (modalState)=>{
    switch(modalState){
        case ModalState.active:
            return styleCardTitle(i18n.orderActive.title);
        case ModalState.expiringSoon:
            return styleCardTitle(i18n.expiringSoon.title, true);
        case ModalState.expiredStillAvailable:
            return styleCardTitle(i18n.expiredStillAvailable.title, true);
        case ModalState.expiredUnavailable:
            return styleCardTitle(i18n.expiredUnavailable.title);
    }
};
